import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Container, Input, Modal, ModalBody } from "reactstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import TabPanel from "components/TabPanel";
import TabView from "components/TabView";
import { validate } from "components/validateGeneralForm";
import Dados from "./Dados";
import Items from "./Items";
import DetalhesConsorcio from "../../../components/DetalhesConsorcio";
import DetalhesEmpresa from "../../../components/DetalhesEmpresa";

import TitleFormWithEdit from "components/TitleFormWithEdit";
import { StatusContratoEnum } from "services/ContratoService";
import { confirm } from "components/Confirmation";
import comboService from "services/ComboService";
import contratoService from "services/ContratoService";
import loteService from "services/LoteService";
import { showLoading } from "components/Loading";
import { hideLoading } from "components/Loading";
import { erroInesperado } from "services/Utils";
import { toast } from "react-toastify";
import Aditivos from "./Aditivos";
import Auditoria from "./Auditoria";
import { TipoContratoEnum, getTipoContratoDescription } from "services/ContratoService";

const tiposContrato = [TipoContratoEnum.NORMAL, TipoContratoEnum.SEMAFORICO].map(el => ({
  id: el,
  nome: getTipoContratoDescription(el)
}))

const layoutsRelatorio = [
  { id: 'normal', nome: 'Normal' },
  { id: 'cet', nome: 'CET' },
]

const tiposValidade = [
  { id: 'fixo', nome: 'Fixo' },
  { id: 'meses', nome: 'Meses' },
]

const opcoesValidadeMeses = [
  { id: 6, nome: '6' },
  { id: 12, nome: '12' },
  { id: 18, nome: '18' },
  { id: 24, nome: '24' },
]

export default function FormContrato() {
  const [data, setData] = useState({})
  const [edit, setEdit] = useState(false)
  const [gestores, setGestores] = useState([])
  const [lotes, setLotes] = useState([])
  const [consorcios, setConsorcios] = useState([])
  const [empresas, setEmpresas] = useState([])
  const [nomeFiscal, setNomeFiscal] = useState('')
  const params = useParams()
  const navigate = useNavigate()
  const formRef = useRef(null)
  const [searchParams] = useSearchParams()

  const id = parseInt(params.id)
  const novo = (0 === id)

  const carregar = () => {
    contratoService.getById(id).then(res => {
      if (res.success) {
        setData({
          ...res.data,
          inicioVigencia: res.data.inicioVigencia ? new Date(res.data.inicioVigencia) : null,
          fimVigencia: res.data.fimVigencia ? new Date(res.data.fimVigencia) : null,
          validade: res.data.tipoValidade === 'fixo' ? (res.data.validade ? new Date(res.data.validade) : null) : null,
          validadeMeses: res.data.tipoValidade === 'meses' ? res.data.validadeMeses : null,
        })
      }
    })
  }

  useEffect(() => {
    if (!!id) {
      carregar()
    } else {
      setData({ tipoValidade: 'fixo' })
    }
  }, [id])

  useEffect(() => {
    if (novo) {
      setEdit(true)
    }
  }, [novo])

  useEffect(() => {
    setNomeFiscal('')
    let cancelado = false
    if (data.idLote) {
      // fiscais atuais do lote (SDD 036): lista, exibida como nomes separados por virgula
      loteService.getFiscalByIdLote(data.idLote).then(res => {
        if (!cancelado && res.success) {
          setNomeFiscal((res.data || []).map(f => f.nome).join(', '))
        }
      }).catch(() => {})
    }
    return () => { cancelado = true }
  }, [data.idLote])

  useEffect(() => {
    comboService.gestorContrato().then(res => {
      if (res.success) {
        setGestores(res.data)
      }
    })

    comboService.consorcio().then(res => {
      if (res.success) {
        setConsorcios(res.data)
      }
    })

    comboService.empresa().then(res => {
      if (res.success) {
        setEmpresas(res.data)
      }
    })

    comboService.lote().then(res => {
      if (res.success) {
        setLotes(res.data)
      }
    })
  }, [])

  const formConfig = useMemo(() => {

    const camposAdicionaisConsorcioEmpresa = []
    if (data.consorcioEmpresa === 'C') {
      camposAdicionaisConsorcioEmpresa.push({ name: "idConsorcio", label: 'Consórcio', type: "combo", options: consorcios, optionValue: 'id', optionLabel: 'nome', showDefault: true, required: true, colspan: 8 })
      if (data.idConsorcio) {
        camposAdicionaisConsorcioEmpresa.push({
          name: 'viewDetalhesConsorcio', type: 'view', component: () => <DetalhesConsorcio idConsorcio={data.idConsorcio} />
        })
      }
    }
    else if (data.consorcioEmpresa === 'E') {
      camposAdicionaisConsorcioEmpresa.push({ name: "idEmpresa", label: 'Empresa', type: "combo", options: empresas, optionValue: 'id', optionLabel: 'razaoSocial', showDefault: true, required: true, colspan: 8 })
      if (data.idEmpresa) {
        camposAdicionaisConsorcioEmpresa.push({
          name: 'viewDetalhesEmpresa', type: 'view', component: () => <DetalhesEmpresa idEmpresa={data.idEmpresa} />
        })
      }
    }

    return [
      [
        { name: "numero", label: 'Número do Contrato', type: "text", required: true, colspan: 4 },
        { name: "numeroProcessoAdm", label: 'Número do Processo Administrativo', type: "text", required: true, colspan: 4 },
        { name: "numeroExpediente", label: 'Número do Expediente', type: "text", required: true, colspan: 4 },
        { name: "objeto", label: 'Objeto', type: "text", required: true, colspan: 8 },
        { name: "statusFmt", label: 'Status', type: "text", colspan: 4, disabled: true },
        { name: "tipoContrato", label: 'Tipo de Contrato', type: "combo", options: tiposContrato, optionValue: 'id', optionLabel: 'nome', colspan: 4 },
        { name: "inicioVigencia", label: 'Início da Vigência', type: "date", required: true, colspan: 2 },
        { name: "fimVigencia", label: 'Fim da Vigência', type: "date", required: true, colspan: 2 },
        { name: "tipoValidade", label: 'Tipo de Validade', type: "combo", options: tiposValidade, optionValue: 'id', optionLabel: 'nome', required: true, colspan: 2 },
        { name: "validade", label: 'Validade', type: "date", required: true, colspan: 2, hide: data.tipoValidade === 'meses' },
        {
          name: "validadeMeses",
          label: 'Validade (meses)',
          type: "combo",
          options: opcoesValidadeMeses,
          optionValue: 'id',
          optionLabel: 'nome',
          showDefault: true,
          required: true,
          colspan: 2,
          hide: data.tipoValidade !== 'meses'
        },
        { name: "idLote", label: 'Lote', type: "combo", options: lotes, optionValue: 'id', optionLabel: 'nome', showDefault: true, required: true, colspan: 4 },
        {
          name: "fiscal", colspan: 8, type: 'view', component: () => {
            return (
              <>
                <label>Fiscal</label>
                <Input type="text" disabled value={nomeFiscal} />
              </>
            )
          }
        },
        { name: "idGestor", label: 'Gestor', type: "combo", options: gestores, optionValue: 'id', optionLabel: 'nome', showDefault: true, required: true, colspan: 6 },
        { name: "layoutRelatorio", label: 'Layout do Relatório', type: "combo", options: layoutsRelatorio, optionValue: 'id', optionLabel: 'nome', colspan: 4, hide: data.tipoContrato == TipoContratoEnum.SEMAFORICO },
      ],
      [
        { separator: true }
      ],
      [
        {
          name: "consorcioEmpresa",
          label: 'Consórcio ou Empresa',
          type: "combo",
          options: [
            { value: 'E', label: 'Empresa' },
            { value: 'C', label: 'Consórcio' },
          ],
          showDefault: true,
          colspan: 4
        },
        ...camposAdicionaisConsorcioEmpresa
      ],
      [
        { separator: true }
      ],
      [
        { name: "valorInicialContratoFmt", label: 'Valor Inicial do Contrato', type: "decimal", required: false, colspan: 4, disabled: true },
        { name: "valorAditamentosFmt", label: 'Valor dos Aditamentos', type: "decimal", required: false, colspan: 4, disabled: true },
        { name: "valorFinalContratoFmt", label: 'Valor Final do Contrato', type: "decimal", required: false, colspan: 4, disabled: true },
      ]
    ]
  }, [gestores, consorcios, empresas, lotes, data, nomeFiscal])

  const handleChange = (field, value) => {
    if (field == 'tipoContrato') {
      setData(data => ({ ...data, [field]: value, consorcioEmpresa: null, idEmpresa: null, idConsorcio: null }))
    } else if (field === 'tipoValidade') {
      setData(data => ({ ...data, tipoValidade: value, validade: null, validadeMeses: null }))
    } else if (field === 'consorcioEmpresa') {
      setData(data => ({ ...data, consorcioEmpresa: value, idEmpresa: null, idConsorcio: null }))
    } else {
      setData(data => ({ ...data, [field]: value }))
    }
  }

  const handleChangeTotal = total => {
    setData(data => ({ ...data, valorInicial: total }))
  }

  const enviar = async (publicar = false) => {

    const errors = validate(formConfig, data)
    if (errors.length > 0) {
      setData(data => ({ ...data, errors }))
      // erros foram encontrados
      return
    }

    const _enviar = async (publicar = false) => {
      try {
        showLoading()
        if (novo) {
          await contratoService.create(data)
        } else {
          await contratoService.update(id, data)
          if (publicar) {
            await contratoService.publish(id)
          }
        }
        toast('Registro salvo com sucesso!')
        hideLoading()
        navigate('/admin/contratos')
      } catch (ex) {
        hideLoading()
        console.error(ex)
        erroInesperado()
      }
    }

    if (publicar) {
      confirm({
        header: "Finalização",
        text: "Ao publicar o contrato não poderá mais fazer alterações, apenas incluir aditivos. Tem certeza que gostaria de continuar?",
        icon: "bi-exclamation-triangle",
        onConfirm: async () => {
          _enviar(true)
        }
      })
    } else {
      _enviar()
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    await enviar()
  }

  const handleDone = () => {
    enviar(true)
  }

  return (
    <Container fluid>
      <div className="mt-3">
        <button type="button" className="btn btn-link p-0 text-muted" onClick={() => navigate('/admin/contratos')}>
          <i className="bi bi-arrow-left" /> Contratos
        </button>
      </div>
      <TitleFormWithEdit title="Contratos" edit={edit} onChangeEdit={setEdit} showButton={!novo && data.idStatus == StatusContratoEnum.RASCUNHO} />
      <TabView defaultTab={searchParams.get('aba') == 'aditivos' ? 3 : 1}>
        <TabPanel label="Dados">
          <form onSubmit={handleSubmit} ref={formRef}>
            <Dados data={data} onChange={handleChange} formConfig={formConfig} edit={edit} />
            {edit && (
              <div className="text-right">
                <Button type="submit" color="primary">Salvar</Button>
                {id != 0 && (
                  <Button type="button" color="primary" onClick={handleDone}>Publicar Contrato</Button>
                )}
              </div>
            )}
          </form>
        </TabPanel>
        <TabPanel label="Itens">
          <Items idContrato={id} onChange={() => carregar()} edit={edit} />
        </TabPanel>
        {id != 0 ? (
          <TabPanel label="Aditivos">
            <Aditivos idContrato={id} edit={edit} />
          </TabPanel>
        ) : null}
        {id != 0 ? (
          <TabPanel label="Auditoria">
            <Auditoria idContrato={id} />
          </TabPanel>
        ) : null}
      </TabView>
    </Container>
  )
}
